import React from "react";
import { Link } from "react-router-dom";
import { FlagshipLockup } from "./brand";
import { Reveal } from "./motion";
import { PostcardSurface } from "./postcard_surface";
import { PhotoPlaceholder } from "./postcard";
import { PriceStamp } from "./stamp";
import { I } from "./icons";
import { useSiteLocale } from "./flow_copy";
import "./seasonal.css";

/* Seasonal send-by pages — harvest integration (2026-10-21).
 *
 * Pattern adapted from the fleet's Lovable ChristmasAd.tsx (code-read and
 * QC-passed in the fleet harvest), rebuilt natively on production
 * components — no embed, no iframe. Production fixes applied at
 * integration, per the harvest work package:
 *
 *   1. The hero carries the real flagship lockup (FlagshipLockup), never
 *      the crowned-bee app icon the Lovable draft used as its page logo.
 *   2. The card front carries production's sample family photo (the same
 *      PhotoPlaceholder the homepage proof uses). The sample message names
 *      Kat, and is labelled a sample by the surface's own chrome.
 *   3. Festive accents are GOLD ONLY. No red, no plaid, no off-palette
 *      seasonal color anywhere on this page.
 *
 * Copy discipline matches the rest of the site: the verified Christmas
 * copy standard (send-by headline, slow-December-mailrooms line, "$3.99
 * flat."), the NOW OPEN badge in English on every locale, no arrival
 * promise we cannot prove, and the word the spec bans never appears —
 * "loved one" throughout. The postcard is bare: nothing on this page
 * shows or implies anything around the card.
 *
 * The pattern is reusable yearly: update SEASON, add the locale strings,
 * and the page re-derives its deadline state on its own.
 *
 * Studio fold-in (2026-10-21, owner-approved "best of the best" pass):
 * the page now follows the ChatGPT Sites Studio page's numbered rhythm —
 * 01 show up (the deadline steps) / 02 local by choice (the Broward
 * section, retitled with Studio's line) / 03 a little peace of mind (a
 * plain-language FAQ). The FAQ answers are Studio's, fact-checked
 * against the governed FAQ before landing; where Studio was vaguer
 * than the governed copy, the governed copy won and Studio's entry was
 * not imported. Section numbers render as literals — locale-neutral,
 * like the NOW OPEN badge.
 */

export const SEASON = {
  key: "christmas-2026",
  // Local calendar dates (constructed as local Dates below, never parsed
  // from the ISO string, so the deadline cannot shift with timezones).
  sendBy: { year: 2026, month: 12, day: 11 },
  ends: { year: 2026, month: 12, day: 25 },
};

const PRICE = "$3.99";

function endOfDay({ year, month, day }) {
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

/* "open"  — on or before the send-by date: the deadline headline runs.
 * "late"  — send-by passed, season still on: honest late copy runs.
 * "closed"— after the season: the homepage banner hides; the page itself
 *           keeps the late copy until SEASON is rolled to the next year. */
export function seasonState(now = new Date()) {
  if (now <= endOfDay(SEASON.sendBy)) return "open";
  if (now <= endOfDay(SEASON.ends)) return "late";
  return "closed";
}

/* The send-by date as a long, locale-native string ("Friday, December 11"
 * / "viernes, 11 de diciembre" / "vandredi 11 desanm"), derived from the
 * single SEASON source so the weekday can never drift from the date. */
export function formatSendBy(locale) {
  const { year, month, day } = SEASON.sendBy;
  const date = new Date(year, month - 1, day);
  // Intl has no Haitian Creole data in most browsers and silently falls
  // back to English ("Friday, December 11"), so Kreyòl is formatted here.
  if (String(locale || "").toLowerCase().startsWith("ht")) {
    const days = ["dimanch", "lendi", "madi", "mèkredi", "jedi", "vandredi", "samdi"];
    const months = ["janvye", "fevriye", "mas", "avril", "me", "jen", "jiyè", "out", "septanm", "oktòb", "novanm", "desanm"];
    return `${days[date.getDay()]} ${day} ${months[month - 1]}`;
  }
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: "long", month: "long", day: "numeric",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en", {
      weekday: "long", month: "long", day: "numeric",
    }).format(date);
  }
}

/* PostcardSurface UI-chrome labels from the active site locale (same set
 * the homepage passes; duplicated here so this module stands alone). */
function cardLabels(t) {
  return {
    front: t.homeProofFront,
    back: t.homeProofBack,
    toggleLabel: t.cardToggleAria,
    note: t.cardNote,
    photoAria: t.cardPhotoAria,
    photoAlt: t.cardPhotoAlt,
    photoEmpty: t.cardPhotoEmpty,
    photoEmptySub: t.cardPhotoEmptySub,
    messageEmpty: t.cardMessageEmpty,
    zoneAddress: t.cardZoneAddress,
    zoneAddressSub: t.cardZoneAddressSub,
    zoneBarcode: t.cardZoneBarcode,
  };
}

function Hero({ state }) {
  const [side, setSide] = React.useState("front");
  const { t, locale } = useSiteLocale();
  const dateStr = formatSendBy(locale);
  const open = state !== "late";
  return (
    <section className="cc-season-hero" aria-labelledby="season-title">
      <div className="cc-shell cc-season-hero-inner">
        <FlagshipLockup width={264} className="cc-season-flagship" />
        {/* BUILD-23: the badge is English on every language. Literal on
            purpose — it is never read from the locale tables. */}
        <span className="cc-season-badge">NOW OPEN</span>
        <p className="cc-eyebrow">{t.seasonalEyebrow}</p>
        <h1 id="season-title" className="cc-display cc-season-title">
          {open ? t.seasonalTitleOpen(dateStr) : t.seasonalTitleLate}
        </h1>
        <p className="cc-lede cc-season-lede">
          {open ? t.seasonalLedeOpen : t.seasonalLedeLate(dateStr)}
        </p>
        <div className="cc-season-actions">
          <Link className="cc-btn cc-btn-primary cc-hero-cta" to="/send">
            {t.seasonalCta} — {PRICE}
          </Link>
        </div>
        <p className="cc-small">{t.seasonalPriceNote}</p>
        <div className="cc-season-stage">
          <PostcardSurface
            id="season-card"
            size="lg"
            side={side}
            onSideChange={setSide}
            labels={cardLabels(t)}
            photoNode={<PhotoPlaceholder />}
            message={t.seasonalCardMessage}
          />
        </div>
      </div>
    </section>
  );
}

function HowDeadline() {
  const { t } = useSiteLocale();
  return (
    <Reveal as="section" className="cc-section cc-season-how" aria-labelledby="season-how-title">
      <div className="cc-shell">
        <span className="cc-season-num" aria-hidden="true">01</span>
        <p className="cc-eyebrow">{t.seasonalHowEyebrow}</p>
        <h2 id="season-how-title" className="cc-h2">{t.seasonalHowTitle}</h2>
        <ol className="cc-how-steps">
          {t.homeHow.map((s, i) => (
            <li key={s.t} className="cc-how-step">
              <span className="cc-how-num" aria-hidden="true">{i + 1}</span>
              <div>
                <h3 className="cc-h3">{s.t}</h3>
                <p className="cc-body">{s.d}</p>
                <p className="cc-how-check"><I.Check size={14} /> {s.check}</p>
              </div>
            </li>
          ))}
        </ol>
        <ul className="cc-checklist cc-season-points">
          {t.seasonalHowBody.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
}

function FamilyPhoto() {
  const { t } = useSiteLocale();
  return (
    <Reveal as="section" className="cc-section cc-season-family" aria-labelledby="season-family-title">
      <div className="cc-shell cc-split">
        <div className="cc-split-copy">
          <span className="cc-season-num" aria-hidden="true">02</span>
          <p className="cc-eyebrow">{t.seasonalLocalEyebrow}</p>
          <h2 id="season-family-title" className="cc-h2">{t.seasonalLocalTitle}</h2>
          <p className="cc-lede">{t.seasonalPhotoCaption}</p>
          <p className="cc-body">{t.seasonalFacilitiesNote}</p>
          <p>
            <Link className="cc-hero-link" to="/facilities">{t.homeFacilitiesTitle}</Link>
          </p>
        </div>
        <div className="cc-split-stage">
          <img
            className="cc-season-photo"
            src="/brand/family-postcard.webp"
            alt={t.seasonalAltFamily}
            width="1200"
            height="800"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </Reveal>
  );
}

function SeasonalFaq() {
  const { t } = useSiteLocale();
  return (
    <Reveal as="section" className="cc-section cc-season-faq" aria-labelledby="season-faq-title">
      <div className="cc-shell">
        <span className="cc-season-num" aria-hidden="true">03</span>
        <p className="cc-eyebrow">{t.seasonalFaqEyebrow}</p>
        <h2 id="season-faq-title" className="cc-h2">{t.seasonalFaqTitle}</h2>
        <div className="cc-season-faq-list">
          {t.seasonalFaq.map((item) => (
            <div key={item.q} className="cc-season-faq-item">
              <h3 className="cc-h3">{item.q}</h3>
              <p className="cc-body">{item.a}</p>
            </div>
          ))}
        </div>
        <p>
          <Link className="cc-hero-link" to="/contact">{t.seasonalFaqHelp}</Link>
        </p>
      </div>
    </Reveal>
  );
}

function FinalCta() {
  const { t } = useSiteLocale();
  return (
    <Reveal as="section" className="cc-section cc-final" aria-labelledby="season-final-title">
      <div className="cc-final-edge" aria-hidden="true" />
      <div className="cc-shell cc-final-inner">
        <h2 id="season-final-title" className="cc-display cc-final-title">{t.seasonalFinalTitle}</h2>
        <PriceStamp t={t} price={PRICE} />
        <Link className="cc-btn cc-btn-primary cc-hero-cta" to="/send">
          {t.seasonalCtaSecondary} — {PRICE}
        </Link>
        <p className="cc-small">{t.seasonalPriceNote}</p>
      </div>
    </Reveal>
  );
}

export function ChristmasPage() {
  const state = seasonState();
  return (
    <div className="cc cc-season">
      <Hero state={state === "open" ? "open" : "late"} />
      <HowDeadline />
      <FamilyPhoto />
      <SeasonalFaq />
      <FinalCta />
    </div>
  );
}

export default ChristmasPage;
